import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseRewardedVideoAdOptions {
  onAdGranted?: () => void;
  onAdDismissed?: () => void;
  onAdFailed?: () => void;
}

/**
 * Shared hook for Google Publisher Tag rewarded video ad integration.
 * Decoupled from any specific widget state — uses callbacks to notify consumers.
 */
export function useRewardedVideoAd(options: UseRewardedVideoAdOptions = {}) {
  const { onAdGranted, onAdDismissed, onAdFailed } = options;
  const [rewardedSlot, setRewardedSlot] = useState<googletag.Slot | null>(null);
  const callbacksRef = useRef(options);
  callbacksRef.current = { onAdGranted, onAdDismissed, onAdFailed };

  const videoUnitAdPath = window.FKL.videoUnitAdPath;
  const googletag = useMemo(() => window.googletag || { cmd: [] }, []);

  const isAdAvailable = videoUnitAdPath && videoUnitAdPath !== "-";

  const initGoogleAdsService = useCallback(
    (slot: googletag.Slot) => {
      slot.addService(googletag.pubads());
      googletag.enableServices();
      googletag.display(slot);
    },
    [googletag]
  );

  const triggerAd = useCallback(() => {
    if (!isAdAvailable) {
      // No ad path configured — treat as dismissed/skipped
      callbacksRef.current.onAdFailed?.();
      return;
    }
    googletag.cmd.push(() => {
      setRewardedSlot(
        googletag.defineOutOfPageSlot(
          videoUnitAdPath,
          googletag.enums.OutOfPageFormat.REWARDED
        )
      );
    });
  }, [googletag, videoUnitAdPath, isAdAvailable]);

  const dismissRewardedAd = useCallback(() => {
    if (rewardedSlot) {
      googletag.pubads().refresh();
      googletag.destroySlots([rewardedSlot]);
      setRewardedSlot(null);
    }
  }, [googletag, rewardedSlot]);

  // Initialize the slot once defined
  useEffect(() => {
    if (rewardedSlot) {
      initGoogleAdsService(rewardedSlot);
    }
  }, [rewardedSlot, initGoogleAdsService]);

  // rewardedSlotReady → show the ad
  useEffect(() => {
    const handler = (event: googletag.events.RewardedSlotReadyEvent) => {
      event.makeRewardedVisible();
    };
    googletag.pubads().addEventListener("rewardedSlotReady", handler);
    return () =>
      googletag.pubads().removeEventListener("rewardedSlotReady", handler);
  });

  // rewardedSlotClosed → user dismissed/skipped the ad
  useEffect(() => {
    const handler = () => {
      dismissRewardedAd();
      callbacksRef.current.onAdDismissed?.();
    };
    googletag.pubads().addEventListener("rewardedSlotClosed", handler);
    return () =>
      googletag.pubads().removeEventListener("rewardedSlotClosed", handler);
  });

  // rewardedSlotGranted → user watched the full ad
  useEffect(() => {
    const handler = () => {
      dismissRewardedAd();
      callbacksRef.current.onAdGranted?.();
    };
    googletag.pubads().addEventListener("rewardedSlotGranted", handler);
    return () =>
      googletag.pubads().removeEventListener("rewardedSlotGranted", handler);
  });

  return { triggerAd, isAdAvailable };
}
